import React, { useEffect, useMemo, useRef, useState } from "react";
import {Box} from "@mui/material";
import { keyframes } from "@mui/system";

const marquee = (distance) => keyframes`
  from { transform: translate3d(0,0,0); }
  to   { transform: translate3d(-${distance}px, 0, 0); }
`;

export default function SMPhrasesScroller({
                                              phrases = [],
                                              iconSrc,
                                              iconAlt = "separator",
                                              iconRepeat = 3,
                                              textColor,

                                              height = 50,

                                              iconSize = {xs: 32, sm: 36, md: 40},

                                              // spacing controls
                                              spaceAfterPhrase = 10,
                                              spaceAfterIcons = 10,

                                              // motion controls
                                              pixelsPerSecond = 50, // lower = slower, higher = faster
                                              pauseOnHover = true,
                                          }) {
    const viewportRef = useRef(null);
    const measureRef = useRef(null);

    const [sequenceWidth, setSequenceWidth] = useState(0);
    const [viewportWidth, setViewportWidth] = useState(0);

    const sequence = useMemo(() => {
        const clean = (phrases || []).filter(Boolean);
        if (!clean.length) return [];

        const items = [];
        clean.forEach((text, idx) => {
            items.push({ type: "text", value: text, key: `t-${idx}` });

            if (idx < clean.length - 1 && iconSrc) {
                items.push({
                    type: "iconGroup",
                    key: `g-${idx}`,
                    count: iconRepeat,
                });
            }
        });

        return items;
    }, [phrases, iconSrc, iconRepeat]);

    useEffect(() => {
        if (!viewportRef.current || !measureRef.current) return;

        const update = () => {
            const vw = viewportRef.current?.getBoundingClientRect()?.width || 0;
            const sw = measureRef.current?.getBoundingClientRect()?.width || 0;
            setViewportWidth(vw);
            setSequenceWidth(sw);
        };

        update();

        // Keep it responsive
        const ro = new ResizeObserver(update);
        ro.observe(viewportRef.current);
        ro.observe(measureRef.current);

        return () => ro.disconnect();
    }, [sequence]);

    if (!sequence.length) return null;

    // Repeat enough times so there is never a “blank” at the end.
    // +2 gives extra buffer so the transition is always seamless.
    const repeats =
        sequenceWidth > 0 && viewportWidth > 0
            ? Math.max(2, Math.ceil(viewportWidth / sequenceWidth) + 2)
            : 3;

    // Duration based on the width we travel (one full sequence width).
    const durationSeconds =
        sequenceWidth > 0 ? Math.max(6, sequenceWidth / pixelsPerSecond) : 20;

    return (
        <Box
            sx={{
                position: "relative",
                backgroundColor: "rgba(255, 255, 255, 0.5)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
                height,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                width: "100%",
            }}
        >
            {/* Viewport */}
            <Box ref={viewportRef} sx={{ width: "100%", overflow: "hidden" }}>
                {/* Track */}
                <Box
                    sx={{
                        display: "flex",
                        width: "max-content",
                        willChange: "transform",
                        animation:
                            sequenceWidth > 0
                                ? `${marquee(sequenceWidth)} ${durationSeconds}s linear infinite`
                                : "none",
                        ...(pauseOnHover && {
                            "&:hover": { animationPlayState: "paused" },
                        }),
                    }}
                >
                {/* Hidden measurer: EXACT same row styling, but not visible */}
                    <Box
                        ref={measureRef}
                        sx={{
                            position: "absolute",
                            visibility: "hidden",
                            pointerEvents: "none",
                            height: 0,
                            overflow: "hidden",
                        }}
                    >
                        <Row
                            items={sequence}
                            iconSrc={iconSrc}
                            iconAlt={iconAlt}
                            iconSize={iconSize}
                            textColor={textColor}
                            spaceAfterPhrase={spaceAfterPhrase}
                            spaceAfterIcons={spaceAfterIcons}
                        />
                    </Box>

                    {/* Render repeated rows */}
                    {Array.from({ length: repeats }).map((_, idx) => (
                        <Row
                            key={`row-${idx}`}
                            items={sequence}
                            iconSrc={iconSrc}
                            iconAlt={iconAlt}
                            iconSize={iconSize}
                            textColor={textColor}
                            spaceAfterPhrase={spaceAfterPhrase}
                            spaceAfterIcons={spaceAfterIcons}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

function Row({
                 items,
                 iconSrc,
                 textColor,
                 iconAlt,
                 iconSize,
                 spaceAfterPhrase,
                 spaceAfterIcons,
             }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            {items.map((item) => {
                if (item.type === "text") {
                    return (
                        <Box
                            key={item.key}
                            sx={{
                                whiteSpace: "nowrap",
                                fontSize: {xs: 14, sm: 20},
                                color: textColor,
                                fontFamily: "'Lora', serif",
                                fontWeight: 600,
                                mr: `${spaceAfterPhrase}px`,
                            }}
                        >
                            {item.value}
                        </Box>
                    );
                }

                // icon group (icons tight side-by-side) + spacing after the group
                return (
                    <Box
                        key={item.key}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            mr: `${spaceAfterIcons}px`,
                        }}
                    >
                        {Array.from({ length: item.count }).map((_, idx) => (
                            <Box
                                key={`${item.key}-${idx}`}
                                component="img"
                                src={iconSrc}
                                alt={iconAlt}
                                sx={{
                                    width: iconSize,
                                    height: iconSize,
                                    display: "block",
                                }}
                            />
                        ))}
                    </Box>
                );
            })}
        </Box>
    );
}
